import { CreateFreeDesign } from "@/types/CreateFreeDesign";
import { SummaryUserSelectionFormData } from "@/types/BuilderEstimatorMapper";

export class BuilderEstimatorMapper {
  static fromUserSelectedOptionsToCreateFreeDesign({
    userSelectedOptions,
    firstName,
    lastName,
    email,
    phone,
    totalPrice,
  }: SummaryUserSelectionFormData): CreateFreeDesign {
    const {
      "Select Design": { houseDesign, houseBuilderId },
      "Select Facade": { facade },
      "Select Inclusions": { inclusionPack },
      "Select Internal Colours": { internalColorScheme },
      "Select External Colours": { externalColorScheme },
      "Optional Upgrades": { optionalUpgrades },
    } = userSelectedOptions;

    if (!houseBuilderId || !houseDesign || !facade) {
      throw new Error(
        "Missing required fields, fromUserSelectedOptionsToCreateFreeDesign"
      );
    }

    return {
      customerName: `${firstName} ${lastName}`,
      customerEmail: email,
      customerPhone: phone,
      builderId: houseBuilderId,
      floorplan: {
        name: houseDesign?.name,
        price: houseDesign?.basePrice,
        imgUrl: houseDesign?.floorplans[0].url,
      },
      facade: facade && {
        name: facade.name,
        price: facade.price,
        imgUrl: facade.url,
      },
      ...(inclusionPack && {
        inclusionPack: {
          name: inclusionPack?.inclusionId?.type,
          price: inclusionPack.price,
        },
      }),
      features: houseDesign?.features,
      ...(internalColorScheme && {
        internalColorScheme: internalColorScheme && {
          name: internalColorScheme.name,
          price: internalColorScheme.price,
          imgUrl: internalColorScheme.imagesUrls[0],
        },
      }),
      ...(externalColorScheme && {
        externalColorScheme: externalColorScheme && {
          name: externalColorScheme.name,
          price: externalColorScheme.price,
          imgUrl: externalColorScheme.imagesUrls[0],
        },
      }),
      ...(optionalUpgrades && {
        optionalUpgrades: optionalUpgrades?.map((upgrade) => ({
          name: upgrade.name,
          price: upgrade.price,
          imgUrl: upgrade.url,
        })),
      }),
      totalPrice: parseFloat(totalPrice.replace(/[^\d.-]/g, "")),
    };
  }

  static fromUserSelectedOptionsToSendEmails(
    data: SummaryUserSelectionFormData
  ) {
    const { userSelectedOptions, firstName, lastName, email, phone } = data;
    const {
      "Select Design": { houseDesign, houseBuilderId },
      "Select Facade": { facade },
      "Select Inclusions": { inclusionPack },
      "Select Internal Colours": { internalColorScheme },
      "Select External Colours": { externalColorScheme },
      "Optional Upgrades": { optionalUpgrades },
    } = userSelectedOptions;

    if (
      !houseBuilderId ||
      !houseDesign ||
      !facade ||
      !firstName ||
      !lastName ||
      !email ||
      !phone
    ) {
      throw new Error(
        "Missing required fields in fromUserSelectedOptionsToSendEmails"
      );
    }

    return {
      userDetails: {
        firstName,
        lastName,
        email,
        phone,
      },
      homeDetails: {
        builderId: houseBuilderId,
        houseName: houseDesign?.name ?? "",
        houseImage: houseDesign?.mainImages[0] ?? "",
      },
      summaryDetails: {
        houseDesign: {
          name: houseDesign?.name ?? "",
          basePrice: houseDesign?.basePrice ?? 0,
          mainImage: houseDesign?.mainImages[0] ?? "",
        },
        facade: {
          name: facade?.name ?? "include",
          price: facade?.price ?? 0,
          url: facade?.url ?? "",
        },
        inclusionPack: {
          type: inclusionPack?.inclusionId?.type ?? "",
          price: inclusionPack?.price ?? 0,
        },
        colorSchemes: {
          external: {
            name: externalColorScheme?.name ?? "-",
            price: externalColorScheme?.price ?? 0,
            url: externalColorScheme?.imagesUrls[0] ?? "",
          },
          internal: {
            name: internalColorScheme?.name ?? "-",
            price: internalColorScheme?.price ?? 0,
            url: internalColorScheme?.imagesUrls[0] ?? "",
          },
        },
        optionalUpgrades: optionalUpgrades ?? [],
      },
    };
  }
}
